<template>
  <v-dialog
    :model-value="modelValue"
    :max-width="smAndDown ? undefined : 520"
    :fullscreen="smAndDown"
    scrollable
    @update:model-value="onDialogToggle"
    @after-enter="onAfterEnter"
    @after-leave="onAfterLeave"
  >
    <v-card class="employee-form-card">
      <v-card-title class="employee-form-title">
        <span>{{ isEdit ? "Edit team member" : "Add team member" }}</span>
        <v-btn
          icon="mdi-close"
          variant="text"
          density="comfortable"
          aria-label="Close"
          :disabled="submitting"
          @click="onCancel"
        />
      </v-card-title>

      <v-card-text class="employee-form-body">
        <p class="employee-form-lede">
          {{
            isEdit
              ? "Update this team member's name or role. To change someone's email address, remove them and re-invite."
              : "We'll create a sign-in for them and send the credentials to the email below."
          }}
        </p>
        <p
          v-if="isEdit && lockRole"
          class="employee-form-self-hint"
          role="status"
        >
          You can update your name, but you can't change your own role.
        </p>

        <v-form
          ref="formRef"
          v-model="formValid"
          validate-on="submit"
          lazy-validation
          @submit.prevent="onSubmit"
        >
          <v-text-field
            ref="nameInputRef"
            v-model="local.name"
            label="Full name"
            variant="outlined"
            density="comfortable"
            :rules="[rules.required]"
            :disabled="submitting"
            autocomplete="name"
          />
          <v-text-field
            v-model="local.email"
            label="Email address"
            variant="outlined"
            density="comfortable"
            type="email"
            :rules="[rules.required, rules.email]"
            :disabled="submitting"
            :readonly="isEdit"
            autocomplete="email"
            class="mt-2"
          />
          <p v-if="isEdit" class="employee-form-self-hint" role="note">
            Email can't be changed - remove and re-invite to use a different one.
          </p>
          <v-select
            v-model="local.role"
            label="Role"
            variant="outlined"
            density="comfortable"
            :items="roleOptions"
            :rules="[rules.required]"
            :disabled="submitting"
            :readonly="lockRole"
            class="mt-2"
          />
          <p v-if="roleDescription" class="employee-form-role-description">
            <span class="employee-form-role-description-label">Permissions:</span>
            {{ roleDescription }}
          </p>
          <p v-if="errorMessage" class="employee-form-error" role="alert">
            {{ errorMessage }}
          </p>
        </v-form>
      </v-card-text>

      <v-card-actions class="employee-form-actions">
        <v-spacer />
        <v-btn
          variant="outlined"
          class="employee-form-cancel-btn"
          :disabled="submitting"
          @click="onCancel"
        >
          Cancel
        </v-btn>
        <v-btn
          color="primary"
          variant="flat"
          class="employee-form-submit-btn"
          :loading="submitting"
          :disabled="submitting"
          @click="onSubmit"
        >
          {{ isEdit ? "Save changes" : "Add team member" }}
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { computed, nextTick, ref, watch } from "vue";
import { useDisplay } from "vuetify";
import { required, email as emailRule } from "@/utils/passwordRules.js";
import { track } from "@/analytics.js";

const EMPTY = { name: "", email: "", role: "Employee" };

export default {
  name: "EmployeeForm",
  props: {
    modelValue: { type: Boolean, default: false },
    mode: { type: String, default: "add" }, // 'add' | 'edit'
    initialValue: {
      type: Object,
      default: () => ({ ...EMPTY }),
    },
    submitting: { type: Boolean, default: false },
    errorMessage: { type: String, default: "" },
    // When true (self-edit), the role select is rendered read-only. Defence
    // against self-demotion is also enforced in employeeHandler.updateEmployee.
    lockRole: { type: Boolean, default: false },
    // Element to restore focus to after the dialog closes. Caller passes
    // the button that opened the dialog so keyboard nav lands sensibly.
    returnFocusTo: { type: [Object, HTMLElement], default: null },
  },
  emits: ["update:modelValue", "submit", "cancel"],
  setup(props, { emit }) {
    const formRef = ref(null);
    const nameInputRef = ref(null);
    const formValid = ref(true);
    const local = ref({ ...EMPTY });
    const display = useDisplay();
    const smAndDown = computed(() => display.smAndDown.value);

    const isEdit = computed(() => props.mode === "edit");

    // Role select intentionally excludes "Owner" - there is exactly one
    // Owner per business and they're set during registration.
    const roleOptions = [
      { title: "Administrator", value: "Administrator" },
      { title: "Employee", value: "Employee" },
    ];

    // Kept in sync with the per-role copy on DashboardEmployeesComponent.vue.
    const ROLE_DESCRIPTIONS = {
      Administrator:
        "Full access to day-to-day features, including remote stamping, branding and reward card settings, and adding, editing or removing team members. Cannot manage the subscription or billing, and cannot change or remove the Owner.",
      Employee:
        "Can scan and stamp cards in person, view customers and see analytics. Cannot stamp remotely, manage the subscription, change branding or reward card settings, or see other team members.",
    };

    const roleDescription = computed(
      () => ROLE_DESCRIPTIONS[local.value.role] || ""
    );

    const rules = { required, email: emailRule };

    const syncFromProps = () => {
      const next = { ...EMPTY, ...(props.initialValue || {}) };
      // The form excludes 'Owner' so reset to Employee if somehow passed.
      if (next.role === "Owner") next.role = "Employee";
      local.value = next;
    };

    watch(
      () => props.modelValue,
      (open) => {
        if (open) {
          syncFromProps();
        }
      }
    );

    watch(
      () => props.initialValue,
      () => {
        if (props.modelValue) syncFromProps();
      },
      { deep: true }
    );

    const onAfterEnter = () => {
      // Focus the first input when the dialog finishes opening, mirroring
      // the accessibility pattern other M4 dialogs already use.
      nextTick(() => {
        const el = nameInputRef.value?.$el?.querySelector("input");
        if (el) el.focus();
      });
    };

    const onAfterLeave = () => {
      // Restore focus to whatever opened the dialog so keyboard / screen
      // reader users land back in the same place they came from.
      const target = props.returnFocusTo;
      if (target && typeof target.focus === "function") {
        try {
          target.focus();
        } catch (e) {
          // Best-effort - silently ignore if the element is gone.
        }
      }
    };

    const onDialogToggle = (open) => {
      if (!open && !props.submitting) emit("update:modelValue", false);
    };

    const onCancel = () => {
      if (props.submitting) return;
      track("dashboard_employee_form_cancelled", { mode: props.mode });
      emit("cancel");
      emit("update:modelValue", false);
    };

    const onSubmit = async () => {
      const result = await (formRef.value && formRef.value.validate
        ? formRef.value.validate()
        : { valid: true });
      if (result && result.valid === false) return;
      emit("submit", {
        name: String(local.value.name || "").trim(),
        email: String(local.value.email || "").trim().toLowerCase(),
        role: local.value.role,
      });
    };

    return {
      formRef,
      nameInputRef,
      formValid,
      local,
      isEdit,
      roleOptions,
      roleDescription,
      rules,
      smAndDown,
      onAfterEnter,
      onAfterLeave,
      onDialogToggle,
      onCancel,
      onSubmit,
    };
  },
};
</script>

<style scoped>
.employee-form-card {
  border-radius: var(--lulu-radius-card);
}

.employee-form-title {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  font-size: 18px;
  font-weight: 800;
  color: var(--lulu-text-strong);
  padding: 20px 20px 8px;
}

.employee-form-body {
  padding: 8px 20px 0 !important;
}

.employee-form-lede {
  font-size: 13px;
  color: var(--lulu-text-muted);
  line-height: 1.5;
  margin: 0 0 16px;
}

.employee-form-error {
  color: var(--lulu-danger);
  font-size: 13px;
  font-weight: 500;
  margin: 12px 0 0;
}

.employee-form-self-hint {
  font-size: 12px;
  color: var(--lulu-text-muted);
  background: var(--lulu-surface-muted);
  border-radius: 8px;
  padding: 8px 12px;
  margin: 0 0 16px;
  line-height: 1.5;
}

.employee-form-role-description {
  font-size: 12px;
  color: var(--lulu-text-muted);
  line-height: 1.5;
  margin: 0 0 12px;
}

.employee-form-role-description-label {
  font-weight: 700;
  color: var(--lulu-text-strong);
}

.employee-form-actions {
  padding: 12px 20px 16px !important;
}

/* Vuetify v-btn defaults to uppercase. The v-dialog teleports outside the
   parent page's :deep selector, so reset text-transform here so the action
   buttons render in sentence case. */
.employee-form-card :deep(.v-btn) {
  text-transform: none !important;
  letter-spacing: 0 !important;
}

/* Primary submit button - matches the header "Get started" pill. */
.employee-form-card :deep(.employee-form-submit-btn) {
  background: rgb(0, 109, 62) !important;
  color: #ffffff !important;
  border: none !important;
  border-radius: 999px !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  padding: 8px 22px !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.2) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.employee-form-card :deep(.employee-form-submit-btn:hover:not(:disabled)) {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.3) !important;
  transform: translateY(-1px);
}

/* Secondary cancel - keep the visual hierarchy (transparent + outline)
   but apply the same hover pop so behaviour is consistent app-wide. */
.employee-form-card :deep(.employee-form-cancel-btn) {
  color: var(--lulu-primary) !important;
  background: transparent !important;
  border: 1px solid rgba(0, 109, 62, 0.4) !important;
  border-radius: 999px !important;
  font-weight: 600 !important;
  font-size: 14px !important;
  padding: 8px 22px !important;
  box-shadow: 0 2px 8px rgba(0, 109, 62, 0.12) !important;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1) !important;
}

.employee-form-card :deep(.employee-form-cancel-btn:hover:not(:disabled)) {
  box-shadow: 0 4px 16px rgba(0, 109, 62, 0.2) !important;
  transform: translateY(-1px);
  border-color: var(--lulu-primary) !important;
}
</style>
